<template>
  <div id="wdw-discuss-thought-form">
    <!-- <button v-if="showBackButton" class="btn btn-primary" @click="closeThoughtForm()">Zurück zur Liste</button> -->
    <div>
      <div v-if="data.errors['all'] != null" class="alert alert-danger" role="alert">
        {{ data.errors['all'] }}
      </div>
      <vue-loading
        v-if="data.loader.fetching_thought"
        :loadingMessage="domainStore.translate('thought.general.message.loading')"
      />
      <div v-else>
        <template v-if="geolocation && geolocation.lat && geolocation.lon">
          <!-- for debugging make lat/lon available in DOM, but do not show visibly in frontend-->
          <span style="display: none">
            {{ 'lat:' + geolocation.lat + ',lon:' + geolocation.lon }}
          </span>
        </template>
        <div v-if="thought && !canEditThought(thought)" class="alert alert-danger" role="alert">
          {{ domainStore.translate('thought.edit.message.no-permission') }}
        </div>
        <div v-else-if="data.errors['thought_not_exist']" class="alert alert-danger" role="alert">
          {{ domainStore.translate('thought.general.message.not-found', { id: thought.id }) }}
        </div>
        <ValidationForm v-else @submit="onSubmitThoughtForm">
          <div id="wdw-thought-form" class="wdw-form" ref="formWrapperElement">
            <h3 v-if="thought">
              {{ domainStore.translate('thought.edit.title', { id: thought.id }) }}
            </h3>

            <form-control-input
              name="title"
              code="title"
              :label="labelTitle"
              prefixId="wdw-discuss-thought-form"
              v-on:validate="onValidatedFormField"
              :defaultvalue="titleDefaultValue"
              :required="true"
            />

            <form-control-textarea
              name="content"
              code="content"
              :label="labelDescription"
              prefixId="wdw-discuss-thought-form"
              :defaultvalue="contentDefaultValue"
              v-on:validate="onValidatedFormField"
              :required="true"
              :textLimitConfig="{
                maxCount: project.thought_maxlength,
                cLimitText: 'Maximale Zeichenanzahl erreicht',
              }"
            />
            <form-control-attributes
              elementPrefixId="wdw-discuss-thought-form"
              v-on:validate="onValidatedFormField"
              :requiredIfResults="data.requiredIfResult"
              :visibleIfResults="data.visibleIfResult"
              :fields="topic.attributes"
              :serverErrors="data.errors"
              :initialValues="attributeEditInitialValues"
              :resetCounters="data.attributeResetCounters"
              :hideOnInitialValueFound="toBool(attributeEditInitialValues['hide_on_initial_value'])"
            />

            <form-control-file
              v-if="toBool(topic.allow_attachments)"
              name="attachment-file"
              code="attachment-file"
              :help="maxAttachmentSizeHint"
              label="Hier können Sie ein Bild hochladen"
              prefixId="wdw-discuss-thought-form"
              accepts="image/*"
              v-on:validate="onValidatedFormField"
            />
            <div class="row" v-if="toBool(topic.allow_attaching_urls)">
              <form-control-input
                name="attach_link_url"
                code="attach_link_url"
                type="link"
                placeholder="https://"
                label="Hier können Sie einen Link anhängen"
                prefixId="wdw-discuss-thought-form"
                v-on:validate="onValidatedFormField"
              />
              <form-control-input
                name="attach_link_title"
                code="attach_link_title"
                label="Titel des Links"
                prefixId="wdw-discuss-thought-form"
                v-on:validate="onValidatedFormField"
              />
            </div>

            <div v-if="thought && thought.files && thought.files.length > 0">
              <p>Bereits angehängte Dateien:</p>
              <p>
                <attachments :files="thought.files" />
              </p>
            </div>

            <form-control-checkbox
              :hidden="!showAnonCheckbox"
              name="anon"
              code="anon"
              label="Ich möchte unerkannt bleiben"
              prefixId="wdw-discuss-thought-form"
              v-on:validate="onValidatedFormField"
              :defaultvalue="tickAnonCheckbox"
            />
            <button
              type="submit"
              :disabled="data.loader.submitting_thought || disableForm"
              class="btn btn-primary btn-form-submit"
              name="Save"
            >
              Speichern
            </button>
            &nbsp;
            <button
              type="button"
              class="btn btn-default btn-form-cancel"
              :disabled="disableForm"
              @click="emitCancelThoughtForm()"
            >
              Abbrechen
            </button>
            <vue-loading v-if="data.loader.submitting_thought" />
          </div>
        </ValidationForm>

        <ThoughtSuggestions
          v-if="project.discuss.show_thought_suggestions == 1 && !thought"
          :topic="topic"
          :query="queryText"
          :config="suggestionsConfig"
          @open-thought="emitOpenThought"
        ></ThoughtSuggestions>
      </div>
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import { onMounted, toRefs, ref, computed, reactive, watch } from 'vue';
import { Form as ValidationForm } from 'vee-validate';
import ThoughtEventMixins from '@/mixins/ThoughtEventMixins';
import useConverter from '@/composables/general/useConverter';
import useThoughtEvents from '@/composables/thought/useThoughtEvents';
import useFormInputDependency from '@/composables/general/useFormInputDependency';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useThoughtConfig from '@/composables/thought/useThoughtConfig';
import { useElementVisibility } from '@vueuse/core';

import { RepositoryFactory } from '@/api/repositoryFactory';

import VueLoading from '@/components/general/Loading';

import FormControlAttributes from '@/components/general/FormControl/Attributes';
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlFile from '@/components/general/FormControl/File';
import FormControlTextarea from '@/components/general/FormControl/TextArea';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox';
import ThoughtSuggestions from '@/components/discuss/ThoughtSuggestions.vue';
import Attachments from '@/components/general/Attachments.vue';

const TopicRepository = RepositoryFactory.get('topic');

// TODO -> fully implement
// [] get thought from store if ID is provided and fill in edit form
// [] get topic from store for settings (attributes)

export default {
  name: 'ThoughtForm',
  mixins: [ThoughtEventMixins],
  components: {
    VueLoading,
    FormControlInput,
    FormControlTextarea,
    FormControlFile,
    FormControlCheckbox,
    FormControlAttributes,
    ValidationForm,
    ThoughtSuggestions,
    Attachments,
  },
  props: {
    thoughtid: {
      type: Number,
      required: false,
    },
    topic: {
      type: Object,
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    geolocation: {
      type: Object,
      required: false,
    },
    disableForm: {
      type: Boolean,
      default: false,
    },
    labelTitle: {
      type: String,
      required: false,
      default: 'Überschrift',
    },
    labelDescription: {
      type: String,
      required: false,
      default: 'Beschreibung',
    },
    attributeDefaultValues: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    suggestionsConfig: {
      type: Object,
      required: false,
      default: () => {
        return {
          disableOpening: true,
          disableEditing: true,
          hideVoting: true,
          hideComments: true,
        };
      },
    },
  },

  setup(props, context) {
    const { topic, geolocation, attributeDefaultValues } = toRefs(props);
    const { emitCancelThoughtForm, emitCloseThought, emitSetMapCenter, emitOpenThought, emitFeedbackMessage } =
      useThoughtEvents(context);
    const { evaluateFormIfCode, getTransitiveDependencyFieldsToBeEmptied, getRemovableAttributeFields } =
      useFormInputDependency();
    const { toBool, bytesToHumanReadable } = useConverter();
    const { l } = useLocalizeMessages();
    const { canEditThought } = useThoughtConfig();
    const thought = ref(null);
    const formWrapperElement = ref(null);
    const queryTitle = ref('');
    const queryContent = ref('');

    // Contains detailed values/files from form
    let formValues = {};
    let uploadedFiles = {};

    const data = reactive({
      // Flags for loader
      loader: {
        fetching_thought: true,
        submitting_thought: false,
      },
      // Server-related errors
      errors: {},
      // Evaluation of visible_if_code and visible_if_value
      visibleIfResult: {},
      // Evaluation of required_if_code and required_if_value
      requiredIfResult: {},
      // Success message
      success: '',
      // thought result after edited / created
      result_thought: null,
      // Map of attribute code-int. Each time the counter increases, it orders the given attribute element to reset/reload its value
      attributeResetCounters: {},
    });

    //--------------------------------COMPUTED-----------------------------------
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const topicAttributes = computed(() => {
      return topic.value.attributes;
    });
    const titleDefaultValue = computed(() => {
      return thought.value == null ? '' : thought.value.title;
    });
    const contentDefaultValue = computed(() => {
      return thought.value == null ? '' : thought.value.content;
    });
    const showAnonCheckbox = computed(() => {
      return toBool(topic.value.anon_thoughts) && isLoggedIn.value;
    });
    const tickAnonCheckbox = computed(() => {
      return toBool(topic.value.anon_thoughts) && !isLoggedIn.value;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const maxAttachmentSizeHint = computed(() => {
      const projectObj = project.value;
      if (projectObj && projectObj.discuss && projectObj.discuss.attachment_max_size) {
        return (
          'Die maximal zulässige Dateigröße beträgt ' + bytesToHumanReadable(projectObj.discuss.attachment_max_size)
        );
      }
      return '';
    });

    const attributeEditInitialValues = computed(() => {
      /**
       * For editing, we take the existing values from thought and
       * apply it as default value of the attribute form controls
       */
      const attrDefValues = attributeDefaultValues.value;
      const existingValues = thought.value ? thought.value.attributes : null;
      if (existingValues == null) {
        return attrDefValues;
      } else {
        const defValues = {};
        const tpcAttrs = topicAttributes.value;
        existingValues.forEach(x => {
          const curAttr = tpcAttrs.filter(a => a.id == x.id);
          if (curAttr.length > 0) {
            if (curAttr[0].type === 'valuelist' || curAttr[0].type === 'select') {
              defValues['attribute-' + x.id] = x.value_all;
            } else if (curAttr[0].type === 'checkbox') {
              defValues['attribute-' + x.id] = toBool(x.value);
            } else {
              defValues['attribute-' + x.id] = x.value;
            }
          } else {
            defValues['attribute-' + x.id] = x.value;
          }
        });

        // Per T17350, defaultValues override other previously assigned ones
        for (const [ak, av] of Object.entries(attrDefValues)) {
          defValues[ak] = av;
        }
        return defValues;
      }
    });

    const queryText = computed(() => {
      return queryTitle.value + ' ' + queryContent.value;
    });

    // Init requiredIfResults
    topicAttributes.value.forEach(attr => {
      data.requiredIfResult[attr.code] = toBool(attr.required);
    });

    if (props.thoughtid) {
      data.loader.fetching_thought = true;
      thoughtStore
        .fetchThought(props.thoughtid)
        .then(resp => {
          thought.value = resp;
          data.errors['thought_not_exist'] = false;
          data.loader.fetching_thought = false;
        })
        .catch(err => {
          // Thought not found
          data.errors['thought_not_exist'] = true;
          data.loader.fetching_thought = false;
        });
    } else {
      data.loader.fetching_thought = false;
    }

    //+------------------------+
    //|     other methods      |
    //+------------------------+

    const closeThoughtForm = () => {
      emitCloseThought(data.result_thought != null);
    };
    const resetForm = formAction => {
      // Reset the form
      formAction.resetForm();
      formValues = {};
      uploadedFiles = {};
    };
    const displayServerErrors = error => {
      try {
        const errData = error.response.data.message;
        const localizationParams = [];
        if (project.value && project.value.discuss && project.value.discuss.attachment_max_size) {
          localizationParams.push(bytesToHumanReadable(project.value.discuss.attachment_max_size));
        }
        data.errors['all'] = l(errData.text, localizationParams);
        // If there are attribute errors, we try to display the attribute's default error message
        // Although this is rarely executed since attribute checking is also done before submitting
        if (errData.attribute_ids) {
          const topicAttrs = topicAttributes.value;
          errData.attribute_ids.forEach(id => {
            const existing = topicAttrs.filter(a => a.id == id);
            if (existing.length > 0) {
              data.errors['attribute-' + id] = existing[0].error;
            }
          });
        }
      } catch (e) {
        data.errors['all'] = 'Fehler: ' + e;
      }
    };
    const onSubmitThoughtForm = (values, actions) => {
      // we can use values for the normal and attribute fields
      // but we need to use uploadedFiles to gain access to the picked files
      data.loader.submitting_thought = true;
      data.errors = {};
      data.success = '';

      // Prepare data and file
      const topicid = topic.value.id;

      const files = new Map();
      for (const [key, value] of Object.entries(uploadedFiles)) {
        files.set(key, value.value);
      }

      if (geolocation.value && geolocation.value.lat && geolocation.value.lon) {
        values.create_lat = geolocation.value.lat;
        values.create_lon = geolocation.value.lon;
      }

      const safelyRemovedFields = getRemovableAttributeFields(topicAttributes.value, values);
      safelyRemovedFields.forEach(f => {
        delete values[f];
      });

      const existingThought = thought.value;
      if (existingThought == null && !props.thoughtid) {
        // Post new thought
        TopicRepository.postNewThought(topicid, values, files)
          .then(response => {
            const respData = response.data.data;
            if (respData.message) {
              data.success = respData.message;
            }
            if (respData.thought) {
              data.result_thought = respData.thought;
            }
            // Reset the form
            resetForm(actions);
            data.loader.submitting_thought = false;

            // emit open-thought
            // anonymous ideas will not have content, so we just show the success message at top without emitting open-thought
            if (respData.thought && respData.thought.id) {
              // Add to store
              thoughtStore.addToThoughts(respData.thought);
              emitFeedbackMessage({
                thought: respData.thought,
                message: respData.message,
                type: 'success',
              });
            }
          })
          .catch(error => {
            displayServerErrors(error);
            data.loader.submitting_thought = false;
          });
      } else {
        // Edit mode. PUT edit
        TopicRepository.putEditThought(topicid, existingThought.id, values, files)
          .then(response => {
            data.result_thought = response.data.data;
            // Try to replace existing one
            thoughtStore.replaceThought(data.result_thought);
            resetForm(actions);
            data.loader.submitting_thought = false;
            // Return back to detail page
            let successMessage = domainStore.translate('thought.edit.message.success-moderated');
            if (data.result_thought.status == 'show') {
              successMessage = domainStore.translate('thought.edit.message.success');
            }
            emitFeedbackMessage({
              thought: data.result_thought,
              message: successMessage,
              type: 'success',
              newThought: false,
            });
          })
          .catch(err => {
            displayServerErrors(err);
            data.loader.submitting_thought = false;
          });
      }
    };

    const onValidatedFormField = fieldValue => {
      if (fieldValue.name == 'title') {
        queryTitle.value = fieldValue.value;
      }
      if (fieldValue.name == 'content') {
        queryContent.value = fieldValue.value;
      }

      if (fieldValue.type === 'file') {
        uploadedFiles[fieldValue.name] = fieldValue;
      } else {
        formValues[fieldValue.name] = fieldValue;

        // T22311: Resolve transitive dependency first before evaluation
        //        fieldsToBeCleared is Set<string> containing field codes
        const fieldsToBeCleared = getTransitiveDependencyFieldsToBeEmptied(
          'visible_if_code',
          'visible_if_value',
          topicAttributes.value,
          fieldValue.code,
          fieldValue.value
        );
        fieldsToBeCleared.forEach(code => {
          const attr = topicAttributes.value.find(x => x.code == code);
          if (attr != null) {
            const attrName = 'attribute-' + attr.id;
            delete formValues[attrName]; // clear the value from formValues
            data.visibleIfResult[attr.code] = false;
            data.requiredIfResult[attr.code] = false;

            // Reset the value of the field itself
            if (attrName in data.attributeResetCounters) {
              data.attributeResetCounters[attrName] += 1;
            } else {
              data.attributeResetCounters[attrName] = 1;
            }
          }
        });

        // Evaluate the whole attribute fields based on its visible_if_code
        data.visibleIfResult = evaluateFormIfCode(
          'visible_if_code',
          'visible_if_value',
          data.visibleIfResult,
          topicAttributes.value,
          fieldValue.code,
          fieldValue.value,
          fieldValue.type
        );
        // Evaluate the whole attribute fields based on its required_if_code
        data.requiredIfResult = evaluateFormIfCode(
          'required_if_code',
          'required_if_value',
          data.requiredIfResult,
          topicAttributes.value,
          fieldValue.code,
          fieldValue.value,
          fieldValue.type
        );
      }
    };
    const imgFileTitle = file => {
      return file.title + ' (' + file.url + ')';
    };

    watch(useElementVisibility(formWrapperElement), (newVisible, oldVisible) => {
      if (newVisible && !oldVisible && formWrapperElement.value) {
        // get first input element inside target to focues
        formWrapperElement.value.querySelectorAll('input')[0].focus();
      }
    });

    return {
      //------------------Vars------------------
      thought,
      data,
      queryText,
      formWrapperElement,
      //---------------Computed-----------------
      titleDefaultValue,
      contentDefaultValue,
      topicAttributes,
      attributeEditInitialValues,
      maxAttachmentSizeHint,
      project,
      //-------------Other methods--------------
      onSubmitThoughtForm,
      onValidatedFormField,
      emitCloseThought,
      emitOpenThought,
      emitCancelThoughtForm,
      imgFileTitle,
      canEditThought,
      toBool,
      showAnonCheckbox,
      tickAnonCheckbox,
      closeThoughtForm,
      domainStore,
    };
  },
};
</script>

<style scoped>
.image-preview {
  max-width: 200px;
  max-height: 150px;
}
</style>
